import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Form, ListGroup, Spinner, Badge } from 'react-bootstrap';
import api from '../../services/api';
import { normalizeHighlightStore, useHighlightedStores } from '../../context/HighlightedStoresContext';

const SEARCH_DEBOUNCE_MS = 320;

/**
 * Суперпользователь: выбор магазинов для красной рамки строк заказов в админских таблицах.
 */
export default function SuperuserHighlightStoresModal({ show, onHide, t }) {
  const { highlightedStores, setHighlightedStores } = useHighlightedStores();
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const [draftIds, setDraftIds] = useState(() => new Set());

  useEffect(() => {
    if (!show) return;
    setDraftIds(new Set(highlightedStores.map((s) => s.id)));
    setQ('');
    setSuggestions([]);
    setSuggestError('');
  }, [show, highlightedStores]);

  const fetchSuggestions = useCallback(async (query) => {
    setLoading(true);
    setSuggestError('');
    try {
      const data = await api.getAdminStoresSearch(query.trim(), 100, {});
      setSuggestions(Array.isArray(data) ? data : []);
    } catch (err) {
      setSuggestions([]);
      setSuggestError(err.message || t('adminHighlight.searchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!show) return undefined;
    const h = setTimeout(() => {
      fetchSuggestions(q);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [q, show, fetchSuggestions]);

  const toggleDraft = (store) => {
    const n = normalizeHighlightStore(store);
    if (!n) return;
    setDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(n.id)) next.delete(n.id);
      else next.add(n.id);
      return next;
    });
  };

  const handleSave = () => {
    const byId = new Map();
    highlightedStores.forEach((s) => {
      const n = normalizeHighlightStore(s);
      if (n) byId.set(n.id, n);
    });
    suggestions.forEach((s) => {
      const n = normalizeHighlightStore(s);
      if (n) byId.set(n.id, n);
    });
    const next = Array.from(draftIds)
      .map((id) => byId.get(id))
      .filter(Boolean);
    setHighlightedStores(next);
    onHide();
  };

  const handleClear = () => {
    setDraftIds(new Set());
    setHighlightedStores([]);
    onHide();
  };

  const selectedInDraft = highlightedStores.filter((s) => draftIds.has(s.id));
  const draftCount = draftIds.size;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t('adminHighlight.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">{t('adminHighlight.intro')}</p>
        <Form.Label className="small fw-semibold">{t('adminHighlight.searchLabel')}</Form.Label>
        <Form.Control
          type="search"
          autoComplete="off"
          placeholder={t('adminHighlight.searchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="mb-2"
        />
        {suggestError && (
          <div className="text-danger small mb-2">{suggestError}</div>
        )}
        {loading && (
          <div className="text-muted small py-2">
            <Spinner animation="border" size="sm" className="me-2" />
            {t('adminHighlight.searching')}
          </div>
        )}
        {!loading && suggestions.length > 0 && (
          <ListGroup className="mb-3" style={{ maxHeight: 240, overflowY: 'auto' }}>
            {suggestions.map((s) => {
              const checked = draftIds.has(s.id);
              return (
                <ListGroup.Item
                  key={s.id}
                  action
                  active={checked}
                  className="d-flex justify-content-between align-items-start gap-2"
                  onClick={() => toggleDraft(s)}
                >
                  <div>
                    <div className="fw-semibold">
                      {s.name || t('adminHighlight.unnamedStore')}{' '}
                      <span className="text-muted font-monospace small">({s.store_id})</span>
                    </div>
                    {s.user_email ? (
                      <div className="small text-muted text-truncate" title={s.user_email}>
                        {s.user_email}
                      </div>
                    ) : null}
                  </div>
                  {checked ? <Badge bg="danger">{t('adminHighlight.selected')}</Badge> : null}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
        {!loading && q.trim() !== '' && suggestions.length === 0 && !suggestError && (
          <p className="text-muted small">{t('adminHighlight.noMatches')}</p>
        )}
        <div className="border rounded p-2 bg-light">
          <div className="small fw-semibold mb-1">{t('adminHighlight.summary')}</div>
          {draftCount === 0 ? (
            <span className="text-muted small">{t('adminHighlight.noneSelected')}</span>
          ) : (
            <ul className="small mb-0 ps-3">
              {Array.from(draftIds).map((id) => {
                const s =
                  selectedInDraft.find((x) => x.id === id) ||
                  suggestions.find((x) => x.id === id);
                if (!s) {
                  return (
                    <li key={id}>
                      id={id} <span className="text-muted">({t('adminHighlight.reopenSearch')})</span>
                    </li>
                  );
                }
                return (
                  <li key={id}>
                    {s.name || s.store_id} — <code>{s.store_id}</code>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap gap-2">
        <Button variant="outline-danger" onClick={handleClear}>
          {t('adminHighlight.clearAll')}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          {t('adminHighlight.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t('adminHighlight.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
